import { Box, Button, Dialog, Typography } from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { FormattedMessage as T } from "react-intl";
import { useMutation, useQueryClient } from "react-query";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { Module, Requirement, Series } from "services/KnowfullyAdminClient";
import { Mode } from "types/common-types";
import ModuleCreateForm from "./ModuleCreateForm";
import OrderableModules from "./OrderableModules";

type ModulesProps = {
  series: Series;
  mode: Mode;
};

const useStyles = makeStyles((theme: Theme) => ({
  rulesHeader: {
    display: "flex",
    justifyContent: "space-between",
    margin: theme.spacing(4, 0, 3, 0),
  },
  ruleListHeader: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(1, 2),
  },
  editActions: {
    // 100 for the action, 48 for the expand button
    width: 148,
  },
  quantity: {
    textAlign: "center",
    width: 80,
  },
  name: {
    flexGrow: 1,
  },
  dragHandle: {
    width: 30,
  },
}));

const ModuleSection = ({ series, mode }: ModulesProps) => {
  const styles = useStyles(useTheme());
  const { seriesService } = useGlobalContext();
  const [createOpen, createOpenActions] = useBoolean(false);
  const queryClient = useQueryClient();

  const isUpdate = mode === Mode.Update;

  const deleteModuleMutation = useMutation<void, unknown, { moduleId: string }>(
    ({ moduleId }) => seriesService.deleteModule(moduleId),
    {
      onSettled: () => {
        queryClient.invalidateQueries(["seriesById", series.id]);
      },
    }
  );

  const reorderModulesMutation = useMutation(
    (orderedModules: Module[]) => {
      const orderedModulesIds = orderedModules.map((m) => m.id);
      return seriesService.updateModulesSequence(series.id, orderedModulesIds);
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(["seriesById", series.id]);
      },
    }
  );

  const reorderRequirementsMutation = useMutation(
    ({
      moduleId,
      orderedRequirements,
    }: {
      moduleId: string;
      orderedRequirements: Requirement[];
    }) => {
      const orderedRequirementIds = orderedRequirements.map((r) => r.id);
      return seriesService.updateRequirementsSequence(
        series.id,
        orderedRequirementIds
      );
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(["seriesById", series.id]);
      },
    }
  );

  const onModuleCreated = () => {
    queryClient.invalidateQueries(["seriesById", series.id]);
    createOpenActions.setFalse();
  };

  return (
    <>
      <Box sx={styles.rulesHeader}>
        <Typography variant="h5">
          <T id="modulesLabel" />
        </Typography>
        <Button
          disabled={!isUpdate}
          onClick={createOpenActions.setTrue}
          startIcon={<AddIcon />}
        >
          <T id="seriesAddModuleButton" />
        </Button>
      </Box>
      <Box sx={styles.ruleListHeader}>
        <Box sx={styles.dragHandle} />
        <Typography variant="subtitle1" sx={styles.name}>
          <T id="moduleLabel" />
        </Typography>
        <Typography variant="subtitle1" sx={styles.quantity}>
          <T id="moduleQuantityLabel" />
        </Typography>
        <Box sx={styles.editActions} />
      </Box>

      <OrderableModules
        modules={series.modules ?? []}
        series={series}
        deleteModuleMutation={deleteModuleMutation}
        reorderModulesMutation={reorderModulesMutation}
        reorderRequirementsMutation={reorderRequirementsMutation}
        mode={mode}
      />

      <Dialog open={createOpen} maxWidth="sm" fullWidth disableEnforceFocus>
        <ModuleCreateForm
          onModuleCreated={onModuleCreated}
          onCancel={createOpenActions.setFalse}
          series={series}
        />
      </Dialog>
    </>
  );
};

export default ModuleSection;
